import React, { useEffect, useState } from 'react';
import {
  API,
  copy,
  showError,
  showInfo,
  showSuccess,
  timestamp2string
} from '../helpers';

import {
  Avatar,
  Button,
  Divider,
  Form,
  Header,
  Input,
  Label,
  Modal,
  Pagination,
  Popup,
  Progress,
  Table,
  Dropdown,
  Checkbox,
  Icon
} from 'semantic-ui-react';
import { Link } from 'react-router-dom';

// 格式化字节大小
function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// 新增时间格式化函数来处理ISO 8601格式
function formatDateTime(dateString) {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    return 'Invalid Date';
  }
}

function renderTimestamp(timestamp) {
  return (
    <>
      {formatDateTime(timestamp)}
    </>
  );
}

function renderRepositoryStatus(repo) {
  let color = 'green';
  let text = '公开';
  
  if (repo.private) {
    color = 'red';
    text = '私有';
  }
  
  if (repo.hidden) {
    color = 'grey';
    text = '隐藏';
  }

  return (
    <Label size={'small'} color={color}>
      {text}
    </Label>
  );
}

const GitRepositoriesTable = () => {
  const [repositories, setRepositories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searching, setSearching] = useState(false);
  const [total, setTotal] = useState(0);
  const [showEdit, setShowEdit] = useState(false);
  const [editingRepo, setEditingRepo] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [repoStats, setRepoStats] = useState(null);
  
  // 新增筛选状态
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [visibilityFilter, setVisibilityFilter] = useState('all');
  const [availableOwners, setAvailableOwners] = useState([]);

  const pageSize = 10;

  // 加载所有者列表
  const loadAllOwners = async () => {
    try {
      const res = await API.get(`/api/admin/git/repositories`, {
        params: {
          page: 1,
          page_size: 1000, // 获取足够多的数据来提取所有者
          search: '',
          owner: 'all',
          visibility: 'all'
        }
      });
      
      const { success, data } = res.data;
      if (success) {
        const owners = [...new Set(data.map(repo => repo.owner).filter(owner => owner))];
        setAvailableOwners(owners);
      }
    } catch (error) {
      console.error('Load owners error:', error);
    }
  };

  const loadRepositories = async (startIdx = 0) => {
    setLoading(true);
    try {
      const res = await API.get(`/api/admin/git/repositories`, {
        params: {
          page: Math.floor(startIdx / pageSize) + 1,
          page_size: pageSize,
          search: searchKeyword,
          owner: ownerFilter,
          visibility: visibilityFilter
        }
      });
      
      console.log('API response:', res.data);
      
      const { success, message, data, total } = res.data;
      if (success) {
        setRepositories(data);
        setTotal(total);
      } else {
        showError(message || '更新失败，未知错误');
      }
    } catch (error) {
      console.error('Load repositories error:', error);
      showError('加载仓库列表失败');
    }
    setLoading(false);
  };

  const onPaginationChange = (e, { activePage }) => {
    (async () => {
      if (activePage === Math.ceil(repositories.length / pageSize) + 1) {
        // In this case we have to load more data and then append them.
        await loadRepositories(activePage - 1);
      }
      setActivePage(activePage);
    })();
  };

  const manualSearchRepositories = async () => {
    await loadRepositories(0);
    setActivePage(1);
  };

  // 当筛选条件改变时重新加载数据
  const handleFilterChange = async () => {
    await loadRepositories(0);
    setActivePage(1);
  };

  // 清除所有筛选条件
  const clearFilters = async () => {
    setSearchKeyword('');
    setOwnerFilter('all');
    setVisibilityFilter('all');
    await loadRepositories(0);
    setActivePage(1);
  };

  const deleteRepository = async (name) => {
    try {
      const res = await API.delete(`/api/admin/git/repository/${name}`);
      const { success, message } = res.data;
      if (success) {
        showSuccess(`仓库 ${name} 删除成功`);
        await loadRepositories(0);
      } else {
        showError(message);
      }
    } catch (error) {
      showError('删除仓库失败');
    }
  };

  const viewStats = async (name) => {
    try {
      const res = await API.get(`/api/admin/git/repository/${name}?action=stats`);
      const { success, message, data } = res.data;
      if (success) {
        setRepoStats(data);
        setShowStats(true);
      } else {
        showError(message);
      }
    } catch (error) {
      showError('获取仓库统计失败');
    }
  };

  const editRepository = (repo) => {
    setEditingRepo({
      name: repo.name,
      description: repo.description || '',
      private: repo.private,
      hidden: repo.hidden
    });
    setShowEdit(true);
  };

  const submitEdit = async () => {
    try {
      console.log('Submitting edit for repo:', editingRepo.name, 'with data:', {
        description: editingRepo.description,
        private: editingRepo.private,
        hidden: editingRepo.hidden
      });
      
      const res = await API.put(`/api/admin/git/repository/${editingRepo.name}`, {
        description: editingRepo.description,
        private: editingRepo.private,
        hidden: editingRepo.hidden
      });
      
      console.log('API response:', res.data);
      
      const { success, message } = res.data;
      if (success) {
        showSuccess('仓库更新成功');
        setShowEdit(false);
        await loadRepositories(0);
      } else {
        showError(message || '更新失败，未知错误');
      }
    } catch (error) {
      console.error('Update repository error:', error);
      console.error('Error response:', error.response?.data);
      
      let errorMessage = '更新仓库失败';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = `更新仓库失败: ${error.message}`;
      }
      
      showError(errorMessage);
    }
  };

  useEffect(() => {
    loadAllOwners();
    loadRepositories(0);
  }, []);

  // 监听筛选条件变化
  useEffect(() => {
    if (ownerFilter !== 'all' || visibilityFilter !== 'all') {
      handleFilterChange();
    }
  }, [ownerFilter, visibilityFilter]);

  return (
    <>
      <Header as='h3'>
        仓库管理
        <Header.Subheader>管理系统中的所有Git仓库</Header.Subheader>
      </Header>
      
      {/* 搜索和筛选区域 */}
      <Form style={{ marginBottom: '20px' }}>
        <Form.Group>
          <Form.Field width={6}>
            <label>搜索</label>
            <Input
              icon='search'
              iconPosition='left'
              placeholder='搜索仓库名称、描述或所有者...'
              value={searchKeyword}
              loading={searching}
              onChange={(e, { value }) => {
                setSearchKeyword(value);
              }}
              action={{
                color: 'blue',
                content: '搜索',
                onClick: manualSearchRepositories
              }}
            />
          </Form.Field>
          <Form.Field width={3}>
            <label>所有者筛选</label>
            <Dropdown
              placeholder='选择所有者'
              fluid
              selection
              clearable
              value={ownerFilter}
              onChange={(e, { value }) => setOwnerFilter(value || 'all')}
              options={[
                { key: 'all', text: '全部所有者', value: 'all' },
                ...availableOwners.map(owner => ({
                  key: owner,
                  text: owner,
                  value: owner
                }))
              ]}
            />
          </Form.Field>
          <Form.Field width={3}>
            <label>可见性筛选</label>
            <Dropdown
              placeholder='选择可见性'
              fluid
              selection
              clearable
              value={visibilityFilter}
              onChange={(e, { value }) => setVisibilityFilter(value || 'all')}
              options={[
                { key: 'all', text: '全部', value: 'all' },
                { key: 'public', text: '公开', value: 'public' },
                { key: 'private', text: '私有', value: 'private' }
              ]}
            />
          </Form.Field>
          <Form.Field width={4}>
            <label>&nbsp;</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button 
                color='grey' 
                onClick={clearFilters}
                icon='remove'
                content='清除筛选'
                style={{ flex: 1 }}
              />
              <Button
                color='green'
                icon='refresh'
                content='刷新'
                onClick={() => loadRepositories(0)}
                style={{ flex: 1 }}
              />
            </div>
          </Form.Field>
        </Form.Group>
      </Form>

      <Divider />
      
      <Table striped selectable compact>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell width={3}>仓库名</Table.HeaderCell>
            <Table.HeaderCell width={3}>描述</Table.HeaderCell>
            <Table.HeaderCell width={2}>所有者</Table.HeaderCell>
            <Table.HeaderCell width={1}>大小</Table.HeaderCell>
            <Table.HeaderCell width={1}>状态</Table.HeaderCell>
            <Table.HeaderCell width={2}>更新时间</Table.HeaderCell>
            <Table.HeaderCell width={3}>操作</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {repositories
            .slice(
              (activePage - 1) * pageSize,
              activePage * pageSize
            )
            .map((repository, idx) => {
              return (
                <Table.Row key={repository.id}>
                  <Table.Cell>
                    <div>
                      <strong style={{ fontSize: '13px' }}>{repository.name}</strong>
                      {repository.mirror && (
                        <Label size='mini' color='blue' style={{ marginLeft: '8px' }}>
                          镜像
                        </Label>
                      )}
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <span style={{ 
                      color: repository.description ? '#333' : '#999',
                      fontStyle: repository.description ? 'normal' : 'italic',
                      fontSize: '12px'
                    }}>
                      {repository.description ? (repository.description.length > 30 ? repository.description.substring(0, 30) + '...' : repository.description) : '无描述'}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span style={{ 
                      color: repository.owner ? '#333' : '#999',
                      fontStyle: repository.owner ? 'normal' : 'italic'
                    }}>
                      {repository.owner || '无所有者'}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    {repository.quota_limit > 0 ? (
                      <Popup
                        trigger={
                          <div style={{ minWidth: '80px' }}>
                            <Progress
                              percent={Math.min(repository.usage_percent || 0, 100)}
                              size='small'
                              color={
                                (repository.usage_percent || 0) > 90 ? 'red' :
                                (repository.usage_percent || 0) > 70 ? 'yellow' : 'green'
                              }
                              style={{ marginBottom: '2px' }}
                            />
                            <div style={{ fontSize: '10px', color: '#666', textAlign: 'center' }}>
                              {formatBytes(repository.size || 0)} / {formatBytes(repository.quota_limit)}
                            </div>
                          </div>
                        }
                        content={
                          <div>
                            <div><strong>当前大小:</strong> {formatBytes(repository.size || 0)}</div>
                            <div><strong>配额上限:</strong> {formatBytes(repository.quota_limit)}</div>
                            <div><strong>使用率:</strong> {(repository.usage_percent || 0).toFixed(1)}%</div>
                          </div>
                        }
                        position='top center'
                      />
                    ) : (
                      <Label size='small' color={repository.size > 10485760 ? 'orange' : 'grey'}>
                        {formatBytes(repository.size || 0)}
                      </Label>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {renderRepositoryStatus(repository)}
                  </Table.Cell>
                  <Table.Cell>
                    <span style={{ fontSize: '11px', color: '#666' }}>
                    {renderTimestamp(repository.updated_at)}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <Button
                        size={'small'}
                        color={'blue'}
                        onClick={() => {
                          window.open(`/repository/${repository.name}`, '_blank');
                        }}
                      >
                        查看
                      </Button>
                      <Button
                        size={'small'}
                        positive
                        onClick={() => {
                          viewStats(repository.name);
                        }}
                      >
                        统计
                      </Button>
                      <Button
                        size={'small'}
                        color={'yellow'}
                        onClick={() => {
                          editRepository(repository);
                        }}
                      >
                        编辑
                      </Button>
                      <Popup
                        trigger={
                          <Button size='small' negative>
                            删除
                          </Button>
                        }
                        on='click'
                        flowing
                        hoverable
                      >
                        <div style={{ textAlign: 'center', padding: '10px' }}>
                          <p>确定删除仓库 <strong>{repository.name}</strong>？</p>
                          <p style={{ color: 'red', fontSize: '12px' }}>此操作不可撤销！</p>
                          <Button
                            color='red'
                            size='small'
                            onClick={() => {
                              deleteRepository(repository.name);
                            }}
                            style={{ marginTop: '8px' }}
                          >
                            确认删除
                          </Button>
                        </div>
                      </Popup>
                    </div>
                  </Table.Cell>
                </Table.Row>
              );
            })}
        </Table.Body>

        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan='7'>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '10px 0'
              }}>
                <div style={{ color: '#666', fontSize: '14px' }}>
                  <strong>总计: {total}</strong> 个仓库
                  {(ownerFilter !== 'all' || visibilityFilter !== 'all' || searchKeyword) && (
                    <span style={{ marginLeft: '10px', color: '#999' }}>
                      (已筛选)
                    </span>
                  )}
                </div>
                <Pagination
                  activePage={activePage}
                  onPageChange={onPaginationChange}
                  size='small'
                  siblingRange={1}
                  totalPages={Math.ceil(total / pageSize)}
                  boundaryRange={1}
                  ellipsisItem={null}
                />
              </div>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>

      {/* 编辑仓库模态框 */}
      <Modal open={showEdit} onClose={() => setShowEdit(false)} size='small'>
        <Modal.Header>
          <Icon name='edit' />
          编辑仓库
        </Modal.Header>
        <Modal.Content>
          {editingRepo && (
            <Form>
              <Form.Field>
                <label>仓库名（只读）</label>
                <Input 
                  value={editingRepo.name} 
                  disabled 
                  icon='github'
                  iconPosition='left'
                />
              </Form.Field>
              <Form.Field>
                <label>描述</label>
                <Input
                  value={editingRepo.description}
                  placeholder='输入仓库描述...'
                  onChange={(e, { value }) => {
                    setEditingRepo({ ...editingRepo, description: value });
                  }}
                />
              </Form.Field>
              <Divider />
              <Form.Field>
                <label>仓库设置</label>
                <div style={{ padding: '10px 0' }}>
                  <div style={{ marginBottom: '15px' }}>
                    <Checkbox
                      checked={editingRepo.private}
                      label={
                        <label style={{ fontSize: '14px' }}>
                          <Icon name='lock' style={{ marginRight: '5px' }} />
                          私有仓库
                          <div style={{ color: '#666', fontSize: '12px', marginTop: '3px' }}>
                            只有仓库所有者和被授权的用户可以访问
                          </div>
                        </label>
                      }
                      onChange={(e, { checked }) => {
                        setEditingRepo({ ...editingRepo, private: checked });
                      }}
                    />
                  </div>
                  <div>
                    <Checkbox
                      checked={editingRepo.hidden}
                      label={
                        <label style={{ fontSize: '14px' }}>
                          <Icon name='eye slash' style={{ marginRight: '5px' }} />
                          隐藏仓库
                          <div style={{ color: '#666', fontSize: '12px', marginTop: '3px' }}>
                            仓库不会在公开列表中显示
                          </div>
                        </label>
                      }
                      onChange={(e, { checked }) => {
                        setEditingRepo({ ...editingRepo, hidden: checked });
                      }}
                    />
                  </div>
                </div>
              </Form.Field>
            </Form>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowEdit(false)}>
            <Icon name='cancel' />
            取消
          </Button>
          <Button positive onClick={submitEdit}>
            <Icon name='save' />
            保存更改
          </Button>
        </Modal.Actions>
      </Modal>

      {/* 仓库统计模态框 */}
      <Modal open={showStats} onClose={() => setShowStats(false)} size='large'>
        <Modal.Header>
          <Icon name='chart bar' />
          仓库统计信息
        </Modal.Header>
        <Modal.Content scrolling>
          {repoStats && (
            <div>
              <Header as='h4' dividing>
                <Icon name='info circle' />
                基本信息
              </Header>
              <Table celled striped>
                <Table.Body>
                  <Table.Row>
                    <Table.Cell width={4}><strong>仓库名</strong></Table.Cell>
                    <Table.Cell>
                      <Icon name='github' />
                      {repoStats.name}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell><strong>描述</strong></Table.Cell>
                    <Table.Cell style={{ 
                      color: repoStats.description ? '#333' : '#999',
                      fontStyle: repoStats.description ? 'normal' : 'italic'
                    }}>
                      {repoStats.description || '无描述'}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell><strong>所有者</strong></Table.Cell>
                    <Table.Cell>
                      {repoStats.owner ? 
                        <span>
                          <Icon name='user' />
                          {repoStats.owner.username} <Label size='mini' color='blue'>ID: {repoStats.owner.id}</Label>
                        </span> : 
                        <span style={{ color: '#999', fontStyle: 'italic' }}>无所有者</span>
                      }
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell><strong>分支数</strong></Table.Cell>
                    <Table.Cell>
                      <Icon name='code branch' />
                      <strong style={{ color: '#2185d0' }}>{repoStats.branch_count}</strong> 个分支
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell><strong>标签数</strong></Table.Cell>
                    <Table.Cell>
                      <Icon name='tag' />
                      <strong style={{ color: '#21ba45' }}>{repoStats.tag_count}</strong> 个标签
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell><strong>创建时间</strong></Table.Cell>
                    <Table.Cell>
                      <Icon name='calendar plus' />
                      {formatDateTime(repoStats.created_at)}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell><strong>更新时间</strong></Table.Cell>
                    <Table.Cell>
                      <Icon name='calendar check' />
                      {formatDateTime(repoStats.updated_at)}
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>

              {repoStats.branches && repoStats.branches.length > 0 && (
                <>
                  <Header as='h4' dividing style={{ marginTop: '30px' }}>
                    <Icon name='code branch' />
                    分支列表
                  </Header>
                  <div style={{ padding: '10px 0' }}>
                    {repoStats.branches.map((branch, idx) => (
                      <Label key={idx} size='small' color='blue' style={{ margin: '3px' }}>
                        <Icon name='code branch' />
                        {branch}
                      </Label>
                    ))}
                  </div>
                </>
              )}

              {repoStats.tags && repoStats.tags.length > 0 && (
                <>
                  <Header as='h4' dividing style={{ marginTop: '30px' }}>
                    <Icon name='tag' />
                    标签列表
                  </Header>
                  <div style={{ padding: '10px 0' }}>
                    {repoStats.tags.map((tag, idx) => (
                      <Label key={idx} size='small' color='green' style={{ margin: '3px' }}>
                        <Icon name='tag' />
                        {tag}
                      </Label>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowStats(false)}>
            <Icon name='close' />
            关闭
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default GitRepositoriesTable; 